import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import moment from 'moment';

import { IconTrash } from '@tabler/icons';

import api from '../../../services/ApiManagement/Index';
import { useToast } from '../../../hooks/toast';
import { useOnlineStatus } from '../../../hooks/onlineStatus';
import getValidationErrors from '../../../utils/getValidationErrors';

import BackHeader from '../../../components/BackHeader';
import AnimalItem from '../../../components/Animal/Item';
import RelativeInput from '../../../components/Animal/RelativeInput';
import Input from '../../../components/Form/Input';
import Select from '../../../components/Form/Select';
import TextArea from '../../../components/Form/TextArea';
import Button from '../../../components/Button';
import { Label, Skeleton, DeleteButton } from '../../../components/Generic';
import { Container, Content, DateInput } from './styles';

interface RegisterData {
  type: string;
  description: string;
  day: string;
  month: string;
  year: string;
  rotulo: string;
  animalCategoria_id: string;
}

const EditRegister: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [animal, setAnimal] = useState<any>({});
  const [register, setRegister] = useState<any>({});
  const [currentType, setCurrentType] = useState('peso');

  const { id_property, id_animal, id_register }: any = useParams();
  const { addToast } = useToast();
  const formRef = useRef<FormHandles>(null);
  const history = useHistory();
  const isOnline = useOnlineStatus();

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    try {
      setLoading(true);

      const animalResponse = await api.get(
        `/animais/${id_animal}`,
        isOnline,
        'animal',
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      setAnimal(animalResponse.data);

      const registerResponse = await api.get(
        `/registros/${id_register}`,
        isOnline,
        'registro',
        {
          headers: {
            animal_id: id_animal,
            propriedade_id: id_property,
          },
        },
      );

      setRegister(registerResponse.data);
      setCurrentType(registerResponse.data.categoria);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: RegisterData) => {
    try {
      setLoadingAction(true);

      formRef.current?.setErrors({});

      const schema = Yup.object().shape({
        day: Yup.string().required('!').min(1, '!').max(31, '!'),
        month: Yup.string().required('!').min(1, '!').max(31, '!'),
        year: Yup.string().required('!').min(1, '!'),
        type: Yup.string().required('Selecione um tipo.'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const time = moment().format('HH:mm:ss');

      await api.put(
        `/registros/${id_register}`,
        {
          animal_id: id_animal,
          data: `${data.year}-${data.month}-${data.day} ${time}`,
          categoria: data.type,
          descricao:
            currentType === 'peso' || currentType === 'perimetro_escrotal'
              ? parseFloat(data.description).toString()
              : data.description,
          rotulo: data.rotulo?.length === 0 ? undefined : data.rotulo,
          animalCategoria_id:
            data.animalCategoria_id?.length === 0
              ? undefined
              : data.animalCategoria_id,
        },
        isOnline,
        {
          headers: {
            id: id_animal,
          },
        },
      );

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Registro alterado.',
      });

      history.goBack();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);

        formRef.current?.setErrors(errors);

        const element = document.getElementById(err.inner[0].path || '');

        if (element !== null) {
          window.scrollTo(0, element.offsetTop - 90);
        }

        return;
      }

      console.log(err);

      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response?.data?.message || 'Algo deu errado!',
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Tem certeza de que quer apagar o registro?')) {
      try {
        setLoadingDelete(true);

        await api.delete(`/registros/${id_register}`, isOnline);

        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Registro excluído.',
        });

        history.goBack();
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message || 'Algo deu errado!',
        });
      } finally {
        setLoadingDelete(false);
      }
    }
  };

  const selectOptions =
    animal.sexo === 'femea'
      ? [
          { value: 'peso', label: 'Peso (kg)' },
          { value: 'vacinacao', label: 'Vacinação' },
          { value: 'inseminacao', label: 'Inseminação' },
          {
            value: 'diagnostico_de_gestacao',
            label: 'Diagnóstico de gestação',
          },
          { value: 'paricao', label: 'Parição' },
          { value: 'observacao', label: 'Observação' },
        ]
      : [
          { value: 'peso', label: 'Peso (kg)' },
          { value: 'vacinacao', label: 'Vacinação' },
          { value: 'perimetro_escrotal', label: 'Perímetro escrotal' },
          { value: 'observacao', label: 'Observação' },
        ];

  return (
    <Container>
      <BackHeader title="Editar registro" />

      {loading ? (
        <LoadingComponent />
      ) : (
        <Content>
          <AnimalItem
            gender={animal.sexo}
            name={animal.nome}
            number={animal.numero_de_identificacao}
            birth={animal.data_de_nascimento}
            imageUrl={animal.imagens?.[0]?.image_url}
            state={animal.estado}
            belongs={animal.pertencente}
            customStyle={{ marginBottom: 30 }}
          />

          <Form ref={formRef} onSubmit={handleSubmit}>
            <Label>Data</Label>
            <DateInput>
              <Input
                name="day"
                label="Dia"
                defaultValue={moment(register.data).format('DD')}
                type="number"
                step="1"
                min="1"
                max="31"
                required={true}
              />
              <Input
                name="month"
                label="Mês"
                defaultValue={moment(register.data).format('MM')}
                type="number"
                step="1"
                min="1"
                max="12"
                required={true}
              />
              <Input
                name="year"
                label="Ano"
                defaultValue={moment(register.data).format('YYYY')}
                type="number"
                step="1"
                min="1"
                required={true}
              />
            </DateInput>

            <Select
              label="Tipo de registro"
              name="type"
              defaultValue={register.categoria}
              disabled={true}
              required={true}
            >
              {selectOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            {register.categoria === 'peso' ? (
              <>
                <Input
                  name="rotulo"
                  label="Rótulo"
                  type="text"
                  disabled={true}
                  defaultValue={register.rotulo}
                />
                <Input
                  name="description"
                  label="Valor"
                  type="number"
                  step="0.01"
                  required={true}
                  defaultValue={register.descricao}
                />
              </>
            ) : register.categoria === 'perimetro_escrotal' ? (
              <Input
                name="description"
                label="Valor"
                type="number"
                step="0.01"
                required={true}
                defaultValue={register.descricao}
              />
            ) : currentType === 'observacao' ? (
              <>
                <Input
                  name="rotulo"
                  label="Rótulo"
                  type="text"
                  required={true}
                  disabled={true}
                  defaultValue={register.rotulo}
                />
                <Input
                  name="description"
                  label="Descrição"
                  required={true}
                  defaultValue={register.descricao}
                />
              </>
            ) : currentType === 'inseminacao' ? (
              <>
                <Label>Animal</Label>
                <RelativeInput
                  path="animalCategoria_id"
                  animalType="macho"
                  property_id={id_property}
                  defaultId={register.animalCategoria_id}
                />
                <Input
                  name="description"
                  label="Descrição"
                  defaultValue={register.descricao}
                />
              </>
            ) : currentType === 'paricao' ? (
              <>
                <Label>Animal</Label>
                <RelativeInput
                  path="animalCategoria_id"
                  property_id={id_property}
                  defaultId={register.animalCategoria_id}
                  onClear={() =>
                    history.push(`/propriedade/${id_property}/animais/novo`)
                  }
                />
                <Input
                  name="description"
                  label="Descrição"
                  defaultValue={register.descricao}
                />
              </>
            ) : (
              <TextArea
                name="description"
                label="Descrição"
                required={true}
                defaultValue={register.descricao}
              />
            )}

            <Button
              isLoading={loadingAction}
              type="submit"
              style={{ marginTop: 60 }}
            >
              Salvar
            </Button>

            <DeleteButton type="button" onClick={handleDelete}>
              <IconTrash size={25} color="#999" style={{ marginRight: 10 }} />
              Excluir
            </DeleteButton>
          </Form>
        </Content>
      )}
    </Container>
  );
};

const LoadingComponent = () => (
  <Content>
    <Skeleton style={{ height: 60, width: '100%', marginBottom: 30 }} />
    <Skeleton style={{ height: 20, width: 100, marginBottom: 10 }} />
    <Skeleton style={{ height: 60, width: '100%', marginBottom: 20 }} />
    <Skeleton style={{ height: 20, width: 100, marginBottom: 10 }} />
    <Skeleton style={{ height: 60, width: '100%', marginBottom: 20 }} />
    <Skeleton style={{ height: 20, width: 100, marginBottom: 10 }} />
    <Skeleton style={{ height: 80, width: '100%', marginBottom: 60 }} />
    <Skeleton style={{ height: 60, width: '100%', marginBottom: 20 }} />
  </Content>
);

export default EditRegister;
